import { useRef, useState } from 'react';
import { Camera, Check, ImageOff, ImagePlus, Search, Sparkles, X } from 'lucide-react';
import {
  PHOTO_STATUS,
  PHOTO_STATUS_LABEL,
  listPhotoEntries,
  patchVariantPhoto,
  patchProductPhoto,
  getPhotoSettings,
  savePhotoSettings,
  mockStandardizePhoto,
  standardizePhotoAI,
  stylizePhotoAI,
  submitPhotoForReview,
  deleteUploadedImage,
  usePhotoTick,
} from '../../utils/photoStatus';
import { fileToDataUrl } from '../../utils/siteConfig';
import { useQueue } from '../../context/QueueContext';
import PhotoReviewModal from './PhotoReviewModal';

const FILTERS = [
  { key: 'todas', label: 'Todas' },
  { key: PHOTO_STATUS.PENDENTE, label: 'Fotos pendentes' },
  { key: PHOTO_STATUS.REVISAO, label: 'Em revisão' },
  { key: PHOTO_STATUS.APROVADA, label: 'Aprovadas' },
];

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

// Busca sem diferenciar maiúsculas nem acentos.
const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

const badgeCls = {
  FOTO_PENDENTE: 'bg-amber-100 text-amber-800',
  FOTO_EM_REVISAO: 'bg-sky-100 text-sky-800',
  FOTO_APROVADA: 'bg-emerald-100 text-emerald-800',
};

export default function AdminPhotos() {
  const { user } = useQueue();
  usePhotoTick();
  const [filter, setFilter] = useState(PHOTO_STATUS.PENDENTE);
  const [review, setReview] = useState(null);
  const [flash, setFlash] = useState('');
  const [error, setError] = useState('');
  const [busyKey, setBusyKey] = useState(null);
  const [selected, setSelected] = useState({});
  const [query, setQuery] = useState('');
  const [batch, setBatch] = useState(null); // { done, total } durante padronização/estilização
  const [batchMode, setBatchMode] = useState(''); // 'padronizar' | 'estilizar'
  const [batchStd, setBatchStd] = useState({});
  const [queue, setQueue] = useState([]);
  const [batchTotal, setBatchTotal] = useState(0);
  const [styledGrid, setStyledGrid] = useState([]); // [{ entry, result }] — grade de revisão do lote estilizado
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const targetRef = useRef(null);

  const settings = getPhotoSettings();
  const entries = listPhotoEntries();
  const isAdmin = user?.role === 'admin';
  const isVendor = user?.role === 'vendedor';
  // Vendedor ajuda nas fotos pendentes: vê apenas "Fotos pendentes"; o restante é do admin.
  const effectiveFilter = isVendor ? PHOTO_STATUS.PENDENTE : filter;
  const q = norm(query.trim());
  const shown = (effectiveFilter === 'todas' ? entries : entries.filter((e) => e.status === effectiveFilter)).filter(
    (e) => !q || norm(`${e.product.name} ${e.variant?.name || ''}`).includes(q)
  );
  const pendingCount = entries.filter((e) => e.status !== PHOTO_STATUS.APROVADA).length;

  const notify = (msg) => {
    setFlash(msg);
    setTimeout(() => setFlash(''), 3000);
  };

  const entryKey = (e) => `${e.product.slug}::${e.variant?.id || 'produto'}`;
  const entryName = (e) => (e.variant ? `a variante “${e.variant.name}”` : `o produto “${e.product.name}”`);
  const entryOriginal = (e) => e.variant?.photoOriginal || e.product.photoOriginal;

  // Variante (produto com variantes) ou produto sem variantes — sem variante fictícia.
  const applyPatch = (entry, patch, action) =>
    entry.variant
      ? patchVariantPhoto(entry.product, entry.variant.id, patch, user, action)
      : patchProductPhoto(entry.product, patch, user, action);

  const pickFile = (entry, source) => {
    targetRef.current = entry;
    (source === 'camera' ? cameraRef : galleryRef).current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !targetRef.current) return;
    const entry = targetRef.current;
    setError('');
    setBusyKey(entryKey(entry));
    try {
      const dataUrl = await fileToDataUrl(file, 1200);
      if (isVendor) {
        // Vendedor: envio direto para "Em revisão" via endpoint de staff — sem modal e
        // sem ações de aprovação (padronizar/estilizar/aprovar/recusar ficam com o admin).
        await submitPhotoForReview(
          entry.product,
          entry.variant?.id || null,
          dataUrl,
          user,
          `Foto original enviada para ${entryName(entry)} — aguardando revisão.`
        );
        notify(`Foto enviada para ${entryName(entry)} — o administrador revisa e publica.`);
        return;
      }
      const swapping = entry.status === PHOTO_STATUS.APROVADA;
      // "Enviar": a foto sobe ao armazenamento durável e entra em revisão; uma foto
      // oficial já aprovada continua pública até a nova ser aprovada.
      await applyPatch(
        entry,
        { photoOriginal: dataUrl, photoStatus: PHOTO_STATUS.REVISAO },
        swapping
          ? `Troca de foto solicitada para ${entryName(entry)} — nova foto aguardando revisão; a foto atual continua no site.`
          : `Foto original enviada para ${entryName(entry)} — aguardando revisão.`
      );
      if (settings.reviewEach) {
        setReview({ entry, dataUrl });
      } else {
        // Salvamento rápido (padrão): a foto fica em revisão — padronizar, estilizar
        // e aprovar acontecem depois, em lote, na aba "Em revisão".
        notify(`Foto salva em revisão para ${entryName(entry)} — estilize/aprove em lote quando quiser.`);
      }
    } catch (err) {
      setError(err.message || 'Falha ao enviar a foto.');
    } finally {
      setBusyKey(null);
      e.target.value = '';
    }
  };

  const openReview = (entry) => {
    const original = entryOriginal(entry);
    if (entry.status === PHOTO_STATUS.REVISAO && original) setReview({ entry, dataUrl: original });
  };

  const approve = async (choice, std) => {
    const { entry, dataUrl } = review;
    const finalImage = choice === 'original' ? dataUrl : std.standard;
    setBusyKey(entryKey(entry));
    setError('');
    try {
      await applyPatch(
        entry,
        {
          image: finalImage,
          photoApproved: finalImage,
          photoThumb: std.thumb,
          photoOriginal: dataUrl,
          photoStatus: PHOTO_STATUS.APROVADA,
          // Origem da imagem aprovada: controla o selo "Foto ilustrativa" no site público.
          photoKind: choice === 'styled' ? 'estilizada' : choice === 'standard' ? 'padronizada' : 'original',
        },
        `Foto original enviada e aprovada (${choice === 'standard' ? 'padronizada' : choice === 'styled' ? 'estilizada por IA' : 'original'}) para ${entryName(entry)}.`
      );
      const rest = queue.slice(1);
      if (rest.length) {
        // Revisão em lote: aprova e já abre a próxima foto selecionada.
        setQueue(rest);
        setReview({ entry: rest[0], dataUrl: entryOriginal(rest[0]), std: batchStd[entryKey(rest[0])] });
        notify('Foto aprovada — próxima do lote.');
      } else {
        const total = batchTotal;
        setQueue([]);
        setBatchTotal(0);
        setSelected({});
        setReview(null);
        notify(total > 1 ? `${total} fotos revisadas e aprovadas em lote.` : 'Foto aprovada.');
      }
    } catch (err) {
      setError(err.message || 'Falha ao salvar a foto.');
    } finally {
      setBusyKey(null);
    }
  };

  // Recusar: a foto enviada é descartada (arquivo removido do servidor) e o item volta
  // para "Foto pendente" — ou para "Aprovada" se era uma TROCA com foto anterior no ar.
  const reject = async () => {
    const fresh = listPhotoEntries().find((e) => entryKey(e) === entryKey(review.entry)) || review.entry;
    const original = entryOriginal(fresh);
    const hadApproved = !!(fresh.variant ? fresh.variant.photoApproved : fresh.product.photoApproved);
    setBusyKey(entryKey(fresh));
    setError('');
    try {
      await applyPatch(
        fresh,
        { photoOriginal: '', photoStatus: hadApproved ? PHOTO_STATUS.APROVADA : PHOTO_STATUS.PENDENTE },
        hadApproved
          ? `Troca de foto recusada para ${entryName(fresh)} — a foto anterior aprovada continua no site.`
          : `Foto recusada para ${entryName(fresh)} — item voltou para a fila de pendentes.`
      );
      if (original) await deleteUploadedImage(original);
      const rest = queue.filter((e) => entryKey(e) !== entryKey(fresh));
      if (rest.length) {
        setQueue(rest);
        setReview({ entry: rest[0], dataUrl: entryOriginal(rest[0]), std: batchStd[entryKey(rest[0])] });
        notify('Foto recusada — próxima do lote.');
      } else {
        setQueue([]);
        setBatchTotal(0);
        setSelected({});
        setReview(null);
        notify(hadApproved ? 'Troca recusada — a foto anterior continua no site.' : 'Foto recusada — item voltou para Fotos pendentes.');
      }
    } catch (err) {
      setError(err.message || 'Falha ao recusar a foto.');
    } finally {
      setBusyKey(null);
    }
  };

  // Elegíveis ao lote: fotos já enviadas e em revisão (têm original no storage).
  const eligible = shown.filter((e) => e.status === PHOTO_STATUS.REVISAO && entryOriginal(e));
  const selectedCount = eligible.filter((e) => selected[entryKey(e)]).length;
  const toggleSelect = (e) => setSelected((prev) => ({ ...prev, [entryKey(e)]: !prev[entryKey(e)] }));
  // "Selecionar todas" marca/desmarca todas as elegíveis VISÍVEIS (respeita a busca).
  const allSelected = eligible.length > 0 && eligible.every((e) => selected[entryKey(e)]);
  const toggleSelectAll = () =>
    setSelected((prev) => {
      const next = { ...prev };
      eligible.forEach((e) => {
        next[entryKey(e)] = !allSelected;
      });
      return next;
    });

  // Padronização em lote: a IA localiza o tecido em cada foto; a revisão/aprovação
  // continua individual (uma a uma) antes de qualquer foto ir ao ar.
  const runBatch = async () => {
    const targets = eligible.filter((e) => selected[entryKey(e)]);
    if (!targets.length || batch) return;
    setError('');
    setBatchMode('padronizar');
    setBatch({ done: 0, total: targets.length });
    const results = {};
    for (let i = 0; i < targets.length; i += 1) {
      const entry = targets[i];
      const original = entryOriginal(entry);
      try {
        results[entryKey(entry)] = await standardizePhotoAI(original, user?.role);
      } catch {
        // IA indisponível para esta foto: padronização simples local, sem bloquear o lote.
        try {
          results[entryKey(entry)] = { ...(await mockStandardizePhoto(original)), ai: false, cropApplied: false };
        } catch {
          results[entryKey(entry)] = null;
        }
      }
      setBatch({ done: i + 1, total: targets.length });
    }
    setBatch(null);
    setBatchMode('');
    const ok = targets.filter((e) => results[entryKey(e)]);
    if (!ok.length) {
      setError('Não foi possível padronizar as fotos selecionadas.');
      return;
    }
    if (ok.length < targets.length) setError(`${targets.length - ok.length} foto(s) não puderam ser processadas e ficaram de fora do lote.`);
    setBatchStd((prev) => ({ ...prev, ...results }));
    setQueue(ok);
    setBatchTotal(ok.length);
    setReview({ entry: ok[0], dataUrl: entryOriginal(ok[0]), std: results[entryKey(ok[0])] });
  };

  // Estilização em lote: gera a versão editorial (tecido drapeado) de todas as fotos
  // selecionadas e abre a GRADE de revisão — cada foto é aprovada/descartada
  // individualmente; aprovadas entram no ar na hora. Cada foto = 1 geração de imagem.
  const runStyledBatch = async () => {
    const targets = eligible.filter((e) => selected[entryKey(e)]);
    if (!targets.length || batch) return;
    setError('');
    setBatchMode('estilizar');
    setBatch({ done: 0, total: targets.length });
    const grid = [];
    let failed = 0;
    for (let i = 0; i < targets.length; i += 1) {
      const entry = targets[i];
      // O provedor de imagem pode falhar de forma transitória (limite/timeout) —
      // 1 nova tentativa automática antes de desistir da foto.
      let result = null;
      for (let attempt = 0; attempt < 2 && !result; attempt += 1) {
        try {
          result = await stylizePhotoAI(entryOriginal(entry), user?.role, entry.product.name, entry.variant?.name || '');
        } catch {
          result = null;
        }
      }
      if (result) grid.push({ entry, result });
      else failed += 1;
      setBatch({ done: i + 1, total: targets.length });
    }
    setBatch(null);
    setBatchMode('');
    if (failed) setError(`${failed} foto(s) não puderam ser estilizadas (provedor de IA) e ficaram de fora da grade.`);
    if (grid.length) setStyledGrid(grid);
    else if (!failed) setError('Nenhuma foto selecionada para estilizar.');
  };

  // Aprovar na grade: entra no ar imediatamente (APROVADA); a foto original real
  // já está no storage (photoOriginal) e não é tocada.
  const approveStyled = async (item) => {
    const { entry, result } = item;
    setBusyKey(entryKey(entry));
    setError('');
    try {
      await applyPatch(
        entry,
        {
          image: result.standard,
          photoApproved: result.standard,
          photoThumb: result.thumb,
          photoStatus: PHOTO_STATUS.APROVADA,
          // Estilizada por IA: o site público exibe o selo "Foto ilustrativa".
          photoKind: 'estilizada',
        },
        `Foto estilizada por IA aprovada (lote) para ${entryName(entry)}.`
      );
      setStyledGrid((g) => g.filter((x) => entryKey(x.entry) !== entryKey(entry)));
      setSelected((prev) => ({ ...prev, [entryKey(entry)]: false }));
      notify('Foto estilizada aprovada.');
    } catch (err) {
      setError(err.message || 'Falha ao salvar a foto.');
    } finally {
      setBusyKey(null);
    }
  };

  // Descartar na grade: a foto volta a aguardar revisão normal (nada é alterado).
  const discardStyled = (item) => {
    setStyledGrid((g) => g.filter((x) => entryKey(x.entry) !== entryKey(item.entry)));
    setSelected((prev) => ({ ...prev, [entryKey(item.entry)]: false }));
  };

  const closeReview = () => {
    setReview(null);
    setQueue([]);
    setBatchTotal(0);
  };

  return (
    <div data-testid="admin-photos-page">
      <h2 className="font-serif text-2xl text-navy">Fotos do Catálogo</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Controle do status de foto por variante — e de produtos sem variantes, no nível do produto.
        As imagens ficam no servidor (armazenamento durável), não no navegador.
      </p>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-navy/10 bg-white p-4">
        <p className="text-sm" data-testid="photos-pending-count">
          <strong className="font-serif text-xl text-navy">{pendingCount}</strong>{' '}
          <span className="text-muted-foreground">item(ns) aguardando foto original</span>
        </p>
        {isAdmin && (
          <label className="flex items-center gap-2 text-xs font-semibold text-navy" data-testid="photos-allow-provisional-label">
            <input
              type="checkbox"
              checked={settings.allowProvisional}
              onChange={(e) => {
                savePhotoSettings({ allowProvisional: e.target.checked }, user);
                notify('Preferência do site público atualizada.');
              }}
              className="h-4 w-4 accent-navy"
              data-testid="photos-allow-provisional"
            />
            Permitir imagem provisória no site enquanto a foto original não chega
          </label>
        )}
        {isAdmin && (
          <label className="flex items-center gap-2 text-xs font-semibold text-navy" data-testid="photos-review-each-label">
            <input
              type="checkbox"
              checked={!!settings.reviewEach}
              onChange={(e) => {
                savePhotoSettings({ reviewEach: e.target.checked }, user);
                notify(e.target.checked ? 'Revisão foto a foto ativada.' : 'Salvamento rápido ativado — fotos novas vão para "Em revisão".');
              }}
              className="h-4 w-4 accent-navy"
              data-testid="photos-review-each"
            />
            Revisar cada foto antes de salvar (padronizar/estilizar na hora)
          </label>
        )}
      </div>

      <div className="relative mt-4">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por produto ou variante…"
          aria-label="Buscar por produto ou variante"
          data-testid="photos-search"
          className="w-full rounded-full border border-navy/15 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition-colors focus:border-navy"
        />
      </div>

      {!isVendor && (
      <div className="no-scrollbar -mx-4 mt-4 flex gap-2 overflow-x-auto px-4" data-testid="photos-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            aria-pressed={f.key}
            data-testid={`photos-filter-${f.key === 'todas' ? 'todas' : f.key.toLowerCase()}`}
            className={`shrink-0 rounded-full border px-4 py-2.5 text-xs font-semibold transition-colors ${
              filter === f.key ? 'border-navy bg-navy text-white' : 'border-navy/20 text-foreground/70 hover:border-navy'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>
      )}

      {isAdmin && eligible.length > 0 && (
        <div className="mt-4 rounded-2xl border border-dashed border-navy/25 bg-white p-4" data-testid="photos-batch-bar">
          <div className="flex items-center justify-between gap-3">
            <p className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="photos-batch-count">
              <Sparkles className="h-4 w-4 text-navy" />
              <span>
                <strong className="text-navy">{selectedCount}</strong> de {eligible.length} foto(s) em revisão selecionada(s)
              </span>
            </p>
            <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-xs font-semibold text-navy" data-testid="photos-select-all-label">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleSelectAll}
                className="h-4 w-4 accent-navy"
                data-testid="photos-select-all"
              />
              Selecionar todas
            </label>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={runBatch}
              disabled={!selectedCount || !!batch}
              data-testid="photos-batch-standardize"
              className="rounded-full bg-navy px-5 py-2.5 text-xs font-bold text-white hover:bg-navy-hover disabled:opacity-40"
            >
              {batch && batchMode === 'padronizar' ? `PADRONIZANDO ${batch.done}/${batch.total}…` : 'PADRONIZAR SELECIONADAS COM IA'}
            </button>
            <button
              type="button"
              onClick={runStyledBatch}
              disabled={!selectedCount || !!batch}
              data-testid="photos-batch-stylize"
              className="rounded-full border-2 border-navy px-5 py-2.5 text-xs font-bold text-navy hover:bg-navy-light disabled:opacity-40"
            >
              {batch && batchMode === 'estilizar' ? `ESTILIZANDO ${batch.done}/${batch.total}…` : 'ESTILIZAR SELECIONADAS COM IA'}
            </button>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Estilizar reapresenta o tecido drapeado (estilo editorial) e consome 1 geração de imagem por foto; a revisão em grade abre ao final.
          </p>
        </div>
      )}

      {flash && (
        <p className="mt-4 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800" data-testid="photos-flash">{flash}</p>
      )}
      {error && (
        <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" role="alert" data-testid="photos-error">{error}</p>
      )}

      <div className="mt-4 space-y-3" data-testid="photos-list">
        {shown.map((entry) => {
          const { product, variant, status } = entry;
          const vId = variant?.id || 'produto';
          const original = entryOriginal(entry);
          // Em revisão com foto enviada: a miniatura mostra a foto real aguardando revisão.
          const img = status === PHOTO_STATUS.REVISAO && original ? original : variant ? variant.image : product.image;
          const busy = busyKey === entryKey(entry);
          const selectable = isAdmin && status === PHOTO_STATUS.REVISAO && !!original;
          return (
            <div key={entryKey(entry)} className="rounded-2xl border border-navy/10 bg-white p-3" data-testid={`photos-row-${product.slug}-${vId}`}>
              <div className="flex items-center gap-3">
                {selectable && (
                  <input
                    type="checkbox"
                    checked={!!selected[entryKey(entry)]}
                    onChange={() => toggleSelect(entry)}
                    aria-label={`Selecionar ${variant?.name || product.name} para processamento em lote`}
                    data-testid={`photos-select-${product.slug}-${vId}`}
                    className="h-4 w-4 shrink-0 accent-navy"
                  />
                )}
                {img ? (
                  <img src={img} alt={variant?.name || product.name} className="h-16 w-16 shrink-0 rounded-xl object-cover" />
                ) : (
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-navy-light">
                    <ImageOff className="h-5 w-5 text-navy/40" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{product.name}</p>
                  {variant ? (
                    <p className="truncate text-xs text-muted-foreground">{variant.name}</p>
                  ) : (
                    <p className="truncate text-xs font-medium text-navy/70" data-testid={`photos-unit-${product.slug}`}>Produto sem variantes</p>
                  )}
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    Cadastro: {fmtDate((variant || product).createdAt)} · por {(variant || product).createdBy || '—'}
                  </p>
                  <span className={`mt-1 inline-block rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${badgeCls[status]}`} data-testid={`photos-status-${product.slug}-${vId}`}>
                    {status === PHOTO_STATUS.PENDENTE ? 'Foto pendente — adicionar foto original' : PHOTO_STATUS_LABEL[status]}
                  </span>
                </div>
              </div>

              {status === PHOTO_STATUS.REVISAO && original ? (
                <button
                  type="button"
                  onClick={() => openReview(entry)}
                  disabled={busy}
                  data-testid={`photos-add-${product.slug}-${vId}`}
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-navy px-4 py-3 text-xs font-bold text-white hover:bg-navy-hover disabled:opacity-50"
                >
                  <Camera className="h-4 w-4" /> {busy ? 'ENVIANDO…' : 'REVISAR FOTO ENVIADA'}
                </button>
              ) : (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => pickFile(entry, 'camera')}
                    disabled={busy}
                    data-testid={`photos-add-${product.slug}-${vId}`}
                    className="flex items-center justify-center gap-2 rounded-xl bg-navy px-3 py-3 text-[11px] font-bold text-white hover:bg-navy-hover disabled:opacity-50"
                  >
                    <Camera className="h-4 w-4" /> {busy ? 'ENVIANDO…' : status === PHOTO_STATUS.APROVADA ? 'TROCAR — TIRAR FOTO' : 'TIRAR FOTO'}
                  </button>
                  <button
                    type="button"
                    onClick={() => pickFile(entry, 'gallery')}
                    disabled={busy}
                    data-testid={`photos-gallery-${product.slug}-${vId}`}
                    className="flex items-center justify-center gap-2 rounded-xl border border-navy/25 px-3 py-3 text-[11px] font-bold text-navy hover:bg-navy-light disabled:opacity-50"
                  >
                    <ImagePlus className="h-4 w-4" /> ESCOLHER DA GALERIA
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {shown.length === 0 && (
          <p className="rounded-2xl border border-dashed border-navy/20 p-8 text-center text-sm text-muted-foreground" data-testid="photos-empty">
            Nenhum item neste filtro.
          </p>
        )}
      </div>

      {/* TIRAR FOTO favorece a câmera traseira (capture="environment"); ESCOLHER DA GALERIA segue disponível. */}
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="sr-only" onChange={onFile} data-testid="photos-file-input" />
      <input ref={galleryRef} type="file" accept="image/*" className="sr-only" onChange={onFile} data-testid="photos-gallery-input" />

      {styledGrid.length > 0 && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" data-testid="photos-styled-grid">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-serif text-lg text-navy">Revisar fotos estilizadas</h3>
                <p className="mt-1 text-xs text-muted-foreground" data-testid="photos-styled-grid-note">
                  Imagens regeneradas pela IA a partir das fotos reais — confira se a cor/textura ficaram fiéis. Aprovar publica na hora; descartar mantém a foto aguardando revisão normal.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setStyledGrid([])}
                aria-label="Fechar grade"
                data-testid="photos-styled-grid-close"
                className="rounded-full p-2 text-navy hover:bg-navy-light"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2" data-testid="photos-styled-grid-list">
              {styledGrid.map((item) => {
                const k = entryKey(item.entry);
                const busy = busyKey === k;
                return (
                  <div key={k} className="rounded-2xl border border-navy/10 p-3" data-testid={`photos-styled-card-${item.entry.product.slug}-${item.entry.variant?.id || 'produto'}`}>
                    <p className="truncate text-sm font-medium text-navy">
                      {item.entry.product.name}
                      {item.entry.variant ? ` — ${item.entry.variant.name}` : ''}
                    </p>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Original</p>
                        <img src={entryOriginal(item.entry)} alt="Foto original" className="mt-1 aspect-square w-full rounded-lg object-cover" />
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Estilizada (IA)</p>
                        <img src={item.result.standard} alt="Foto estilizada pela IA" className="mt-1 aspect-square w-full rounded-lg object-cover" />
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => approveStyled(item)}
                        disabled={busy}
                        data-testid={`photos-styled-approve-${item.entry.product.slug}-${item.entry.variant?.id || 'produto'}`}
                        className="flex items-center justify-center gap-1.5 rounded-full bg-navy px-3 py-2.5 text-[11px] font-bold text-white hover:bg-navy-hover disabled:opacity-50"
                      >
                        <Check className="h-3.5 w-3.5" /> {busy ? 'PUBLICANDO…' : 'APROVAR'}
                      </button>
                      <button
                        type="button"
                        onClick={() => discardStyled(item)}
                        disabled={busy}
                        data-testid={`photos-styled-discard-${item.entry.product.slug}-${item.entry.variant?.id || 'produto'}`}
                        className="flex items-center justify-center gap-1.5 rounded-full border border-navy/25 px-3 py-2.5 text-[11px] font-bold text-navy hover:bg-navy-light disabled:opacity-50"
                      >
                        <X className="h-3.5 w-3.5" /> DESCARTAR
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {review && (
        <PhotoReviewModal
          original={review.dataUrl}
          variantName={review.entry.variant?.name || review.entry.product.name}
          productName={review.entry.product.name}
          precomputed={review.std || null}
          queueInfo={queue.length ? { index: batchTotal - queue.length + 1, total: batchTotal } : null}
          onChoose={approve}
          onReject={reject}
          onRetake={() => {
            const entry = review.entry;
            closeReview();
            pickFile(entry, 'camera');
          }}
          onClose={closeReview}
        />
      )}
    </div>
  );
}
